'use client';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Minus, Square, Copy, X, RotateCw } from 'lucide-react';
import { IconButton } from './icon-button';
import { useDraggableRegions } from '../../util/hook-draggable-regions';
import { getIsWindowMaximized } from '../../util/get-is-window-maximized';
// Rendered in the App Shell outside `NextIntlClientProvider`: next-intl's router needs that context
// (and reads the route params, which blocks the shell). Only back/forward/refresh, no paths.
// eslint-disable-next-line no-restricted-imports -- see above
import { useRouter } from 'next/navigation';

export default function AppBar() {
  const router = useRouter();

  const barRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const winRef = useRef<HTMLDivElement>(null);

  const [isMax, setIsMax] = useState(false);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let mounted = true;
    let raf = 0;

    const update = async () => {
      try {
        const next = await getIsWindowMaximized();
        if (mounted) setIsMax((prev) => (prev !== next ? next : prev));
      } catch {}
      if (mounted) setScale(window.devicePixelRatio || 1);
    };

    const onResize = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    update();
    window.addEventListener('resize', onResize);
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    mq.addEventListener?.('change', onResize);

    return () => {
      mounted = false;
      window.removeEventListener('resize', onResize);
      mq.removeEventListener?.('change', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useDraggableRegions(barRef, [navRef, winRef], scale);

  const goBack = () => router.back();
  const goForward = () => window.history.forward();
  const reload = () => router.refresh();
  const minimize = () => window.wildtangent?.MinimizeWindow();
  const close = () => window.wildtangent?.CloseWindow();

  const toggleMaximize = () => {
    const wt = window.wildtangent;
    if (!wt) return;
    if (isMax) {
      wt.RestoreWindow?.();
      setIsMax(false);
    } else {
      wt.MaximizeWindow?.();
      setIsMax(true);
    }
  };

  const onBarDoubleClick = () => toggleMaximize();

  const wtIconProps = { strokeWidth: 2, absoluteStrokeWidth: true };
  return (
    <div
      ref={barRef}
      onDoubleClick={onBarDoubleClick}
      className={`bg-background flex h-[40px] w-full items-center px-1 text-neutral-200 shadow-xs select-none`}
      role='banner'
    >
      {/* Left: Navigation */}
      <div ref={navRef} className='flex items-center gap-1 pr-1'>
        <IconButton label='Back' onClick={goBack}>
          <ArrowLeft {...wtIconProps} className='h-5 w-5' />
        </IconButton>
        <IconButton label='Forward' onClick={goForward}>
          <ArrowRight {...wtIconProps} className='h-5 w-5' />
        </IconButton>
        <IconButton label='Reload' onClick={reload}>
          <RotateCw {...wtIconProps} className='h-5 w-5' />
        </IconButton>
      </div>

      {/* Right: Window Controls */}
      <div ref={winRef} className='ml-auto flex items-center'>
        <IconButton label='Minimize' onClick={minimize}>
          <Minus {...wtIconProps} className='h-5 w-5' />
        </IconButton>
        <IconButton label={isMax ? 'Restore' : 'Maximize'} onClick={toggleMaximize}>
          {isMax ? (
            <Copy {...wtIconProps} className='h-4 w-4 [transform:scaleX(-1)]' />
          ) : (
            <Square {...wtIconProps} className='h-4 w-4' />
          )}
        </IconButton>
        <IconButton label='Close' onClick={close} danger>
          <X {...wtIconProps} className='h-5 w-5' />
        </IconButton>
      </div>
    </div>
  );
}
